import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import type { JSX, ReactNode } from "react";
import { z } from "zod";

import Confetti from "@repo/icons/colored/Confetti";
import SwitchHorizontal from "@repo/icons/default/SwitchHorizontal";
import { CpsAvatar } from "corpus";

import { BatchPatientName } from "@/components/batch-adjust/batch-patient-name";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  type BatchAdjustTarget,
  batchAdjustAppliedIdsAtom,
  batchAdjustOriginAtom,
  batchAdjustTargetsAtom,
} from "@/lib/atoms/batch-adjust-form-atom";
import {
  addMinutesToTime,
  type BatchAdjustDirection,
  directionToDelta,
  toDisplayDate,
  toDisplayTime,
} from "@/lib/helpers/batch-adjust";
import { getNameInitials } from "@/lib/string";

const searchSchema = z.object({
  action: z.enum(["adjust", "cancel"]).catch("adjust"),
  direction: z.enum(["advance", "delay"]).optional(),
  minutes: z.coerce.number().optional(),
  count: z.coerce.number(),
});

const STEPS_SINCE_ORIGIN = { adjust: 4, cancel: 3 } as const;

interface SuccessAppointmentRowProps {
  target: BatchAdjustTarget;
  children: ReactNode;
}

const SuccessAppointmentRow = ({
  target,
  children,
}: SuccessAppointmentRowProps): JSX.Element => (
  <div className="flex flex-row items-center gap-3">
    <CpsAvatar
      alt={target.nomePaciente}
      color="primaryLight"
      initials={getNameInitials(target.nomePaciente) ?? ""}
      size={40}
    />
    <div className="flex min-w-0 flex-1 flex-col">
      <BatchPatientName
        nomePaciente={target.nomePaciente}
        recorrente={target.recorrente}
      />
      <span className="flex items-center gap-2 text-neutral-500 text-xs">{children}</span>
    </div>
  </div>
);

const adjustedSummary = (
  total: number,
  direction: BatchAdjustDirection,
  minutes: number,
): string => {
  const plural = total === 1 ? "" : "s";
  const verb = direction === "advance" ? "adiantado" : "atrasado";
  return `${total} agendamento${plural} ${verb}${plural} em ${minutes} minutos, mantendo a sequência.`;
};

const cancelledSummary = (total: number): string =>
  total === 1 ? "1 agendamento cancelado." : `${total} agendamentos cancelados.`;

const BatchAdjustSuccessPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const {
    action,
    direction = "delay",
    minutes = 0,
    count,
  } = useSearch({
    from: "/calendar/batch-adjust/success",
  });
  const targets = useAtomValue(batchAdjustTargetsAtom);
  const appliedIds = useAtomValue(batchAdjustAppliedIdsAtom);
  const origin = useAtomValue(batchAdjustOriginAtom);

  const isCancel = action === "cancel";
  const delta = directionToDelta(direction, minutes);
  const applied = targets.filter((target) => appliedIds.includes(target.codAgendamento));
  const unchanged = targets.filter(
    (target) => !appliedIds.includes(target.codAgendamento),
  );
  const total = applied.length > 0 ? applied.length : count;

  const handleFinish = (): void => {
    if (targets.length === 0) {
      void navigate({ to: origin, replace: true });
      return;
    }
    router.history.go(-STEPS_SINCE_ORIGIN[action]);
  };

  return (
    <RoundedLayout className="-mb-[env(safe-area-inset-bottom)]">
      <Page className="flex flex-col">
        <div className="flex h-full flex-col justify-between gap-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <Confetti size={96} />
              <h1 className="font-semibold text-neutral-700 text-xl">
                {isCancel
                  ? "Os agendamentos foram cancelados"
                  : "Os agendamentos foram alterados com sucesso"}
              </h1>
              <p className="text-neutral-500">
                {isCancel
                  ? cancelledSummary(total)
                  : adjustedSummary(total, direction, minutes)}
              </p>
            </div>

            {applied.length > 0 ? (
              <div className="rounded-lg border-[1.5px] border-neutral-100 p-4">
                <p className="mb-3 border-neutral-100 border-b pb-3 font-medium text-neutral-700 text-sm">
                  {toDisplayDate(applied[0].data)}
                </p>
                <div className="flex flex-col divide-y divide-neutral-100 *:py-2 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
                  {applied.map((target) => (
                    <SuccessAppointmentRow key={target.codAgendamento} target={target}>
                      <span className="line-through">
                        {toDisplayTime(target.horaInicio)}
                      </span>
                      {isCancel ? (
                        <span className="font-medium text-danger-500">Cancelado</span>
                      ) : (
                        <>
                          <SwitchHorizontal
                            size={12}
                            className="shrink-0 fill-neutral-300"
                          />
                          <span className="font-medium text-secondary-500">
                            {toDisplayTime(addMinutesToTime(target.horaInicio, delta))}
                          </span>
                        </>
                      )}
                    </SuccessAppointmentRow>
                  ))}
                </div>
              </div>
            ) : null}

            {!isCancel && unchanged.length > 0 ? (
              <div className="rounded-lg border-[1.5px] border-neutral-100 p-4">
                <p className="mb-3 border-neutral-100 border-b pb-3 font-medium text-neutral-700 text-sm">
                  Continuam no horário atual
                </p>
                <div className="flex flex-col divide-y divide-neutral-100 *:py-2 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
                  {unchanged.map((target) => (
                    <SuccessAppointmentRow key={target.codAgendamento} target={target}>
                      {toDisplayTime(target.horaInicio)} - {toDisplayTime(target.horaFim)}
                    </SuccessAppointmentRow>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="sticky bottom-0 flex flex-col gap-2 bg-white pt-2 pb-2">
            <Button fullWidth onClick={handleFinish}>
              {origin === "/waiting-room" ? "Ver sala de espera" : "Ver agenda"}
            </Button>
          </div>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/calendar/batch-adjust/success")({
  component: BatchAdjustSuccessPage,
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (!context.flags["ajuste-em-lote-de-agendamentos"]) {
      redirect({ to: "/calendar", throw: true });
    }
  },
});
